import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../config';
import { companiesRef } from './read';
import { usersRef } from '../user/read';
import { companyStatus, registerStatus } from '../../../../constants/enum';

/**
 * 招待中・利用停止中の企業を論理削除する (#2802)
 *
 * 処理内容:
 * 1. companies の status を deleted に更新
 * 2. 関連 users (代表ユーザー = invited_email と一致する招待中ユーザー) の register_status を deleted に更新
 * 3. companyInvitations の招待トークンを used=true にして無効化
 *
 * 招待中・利用停止中の企業のみ対象。それ以外の企業に呼ぶことを想定していない。
 *
 * invitedEmail が無い場合は本登録完了済み（= 想定外）の可能性が高く、
 * 企業のみ削除してユーザー/招待トークンが取り残される不整合を防ぐためエラーとする。
 */
export const deleteInvitedCompany = async (
  companyId: string,
  invitedEmail: string | null | undefined,
  updatedBy: string,
): Promise<void> => {
  if (!invitedEmail) {
    throw new Error('deleteInvitedCompany: invitedEmail is required');
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();

  // 1. 企業を論理削除
  const companyDocRef = doc(companiesRef, companyId);
  batch.update(companyDocRef, {
    status: companyStatus.deleted,
    updated_at: now,
    updated_by: updatedBy,
  });

  // 2. 関連 users を論理削除（招待中ユーザー = invited_email と一致 & emailRegistered）
  const userQuery = query(
    usersRef,
    where('company_id', '==', companyId),
    where('email', '==', invitedEmail),
    where('register_status', '==', registerStatus.emailRegistered),
  );
  const userSnapshot = await getDocs(userQuery);
  userSnapshot.docs.forEach(userDoc => {
    batch.update(userDoc.ref, {
      register_status: registerStatus.deleted,
      updated_at: now,
      updated_by: updatedBy,
    });
  });

  // 3. companyInvitations のトークンを無効化
  const invitationsRef = collection(db, 'companyInvitations');
  const invitationQuery = query(
    invitationsRef,
    where('company_id', '==', companyId),
    where('used', '==', false),
  );
  const invitationSnapshot = await getDocs(invitationQuery);
  invitationSnapshot.docs.forEach(invDoc => {
    batch.update(invDoc.ref, {
      used: true,
      updated_at: now,
    });
  });

  await batch.commit();
};
